import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, CheckCircle2, XCircle, FileDown, Wrench } from "lucide-react";
import { toast } from "sonner";
import RobotsTag from "../seo/RobotsTag";
import { QUOTE_STATUS_META } from "../../lib/quoteConstants";
import { fmtEUR, isNoVat, vatMention } from "../../lib/invoiceConstants";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card } from "../ui/card";
import { Textarea } from "../ui/textarea";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/quotes`;

const PublicQuotePage = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null); // null=loading, false=error
  const [decision, setDecision] = useState(null); // 'accept' | 'refuse'
  const [form, setForm] = useState({ validatorName: "", validatorEmail: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/${token}`);
      if (!res.ok) { setQuote(false); return; }
      setQuote(await res.json());
    } catch {
      setQuote(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submitDecision = async (e) => {
    e.preventDefault();
    if (!form.validatorName || !form.validatorEmail) { toast.error("Nom et email requis."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE}/${token}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, ...form }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Erreur"); return; }
      setQuote(data);
      setDecision(null);
      toast.success(decision === "accept" ? "Devis accepté. Merci !" : "Devis refusé.");
    } catch {
      toast.error("Une erreur est survenue.");
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-100 py-10 px-4">
      <RobotsTag content="noindex, nofollow" />
      <Helmet><title>Votre devis – NGL SERVICES</title></Helmet>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="bg-[hsl(207,78%,42%)] rounded-lg p-2"><Wrench className="w-5 h-5 text-white" /></div>
          <span className="font-bold text-slate-800">NGL SERVICES</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (quote === null) {
    return <Shell><div className="flex justify-center p-10"><Loader2 className="w-8 h-8 animate-spin text-[hsl(207,78%,42%)]" /></div></Shell>;
  }
  if (quote === false) {
    return <Shell><Card className="p-8 text-center" data-testid="quote-not-found"><XCircle className="w-10 h-10 mx-auto text-red-400 mb-3" /><p>Devis introuvable ou lien invalide.</p></Card></Shell>;
  }

  const meta = QUOTE_STATUS_META[quote.status] || {};
  const isFinal = ["accepte", "refuse", "converti_en_facture", "expire"].includes(quote.status);
  const noVat = isNoVat(quote.seller?.vatRegime);

  return (
    <Shell>
      <Card className="overflow-hidden" data-testid="public-quote-card">
        <div className="bg-[hsl(215,25%,15%)] text-white p-6">
          <p className="text-sm text-slate-300">Devis</p>
          <p className="text-2xl font-bold font-mono">{quote.number || "—"}</p>
          {quote.title && <p className="text-slate-200 mt-1">{quote.title}</p>}
          <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded border ${meta.color || ""}`}>{meta.label || quote.status}</span>
        </div>

        <div className="p-6 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-slate-700">Émetteur</p>
              <p>{quote.seller?.name}</p>
              {quote.seller?.siret && <p className="text-xs text-muted-foreground">SIRET : {quote.seller.siret}</p>}
            </div>
            <div>
              <p className="font-semibold text-slate-700">Client</p>
              <p>{quote.buyer?.name}</p>
            </div>
          </div>
          {quote.validUntil && <p className="text-xs text-muted-foreground">Valable jusqu'au {quote.validUntil}</p>}

          <div className="border-t pt-3 space-y-1">
            {quote.lines?.map((l, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span>{l.designation} <span className="text-muted-foreground">×{l.quantity}{!noVat ? ` (TVA ${l.vatRate}%)` : ""}</span></span>
                <span className="font-medium">{fmtEUR(l.totalHT)}</span>
              </div>
            ))}
          </div>
          <div className="bg-slate-50 rounded p-3 ml-auto sm:w-64 space-y-1">
            <div className="flex justify-between"><span>Total HT</span><span>{fmtEUR(quote.totalHT)}</span></div>
            {!noVat && quote.vatBreakdown?.map((b) => (<div key={b.rate} className="flex justify-between text-xs text-muted-foreground"><span>TVA {b.rate}%</span><span>{fmtEUR(b.vatAmount)}</span></div>))}
            <div className="flex justify-between font-bold text-[hsl(207,78%,42%)] border-t pt-1"><span>{noVat ? "Net à payer" : "Total TTC"}</span><span data-testid="public-quote-ttc">{fmtEUR(noVat ? quote.totalHT : quote.totalTTC)}</span></div>
          </div>
          {noVat && vatMention(quote.seller?.vatRegime) && (
            <p className="text-xs text-muted-foreground">{vatMention(quote.seller?.vatRegime)}</p>
          )}

          <a href={`${BASE}/${token}/pdf`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" data-testid="public-pdf-button"><FileDown className="w-4 h-4 mr-2" /> Télécharger le PDF</Button>
          </a>

          {/* Validation déjà effectuée */}
          {quote.validation && (
            <div className={`rounded-lg p-3 ${quote.validation.decision === "accepte" ? "bg-emerald-50" : "bg-red-50"}`} data-testid="public-validation-result">
              <p className="font-semibold flex items-center gap-1">
                {quote.validation.decision === "accepte" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                Devis {quote.validation.decision === "accepte" ? "accepté" : "refusé"}
              </p>
              <p className="text-xs text-muted-foreground">Par {quote.validation.validatorName} le {(quote.validation.at || "").slice(0, 16).replace("T", " ")}</p>
            </div>
          )}

          {/* Boutons de validation */}
          {!isFinal && !quote.validation && (
            <div className="border-t pt-4">
              {!decision ? (
                <div className="flex gap-3">
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => setDecision("accept")} data-testid="accept-quote-button">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Accepter le devis
                  </Button>
                  <Button variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" onClick={() => setDecision("refuse")} data-testid="refuse-quote-button">
                    <XCircle className="w-4 h-4 mr-2" /> Refuser
                  </Button>
                </div>
              ) : (
                <form onSubmit={submitDecision} className="space-y-3" data-testid="validation-form">
                  <p className="font-medium">{decision === "accept" ? "Confirmer l'acceptation" : "Confirmer le refus"}</p>
                  <div className="space-y-1"><Label className="text-xs">Votre nom *</Label><Input value={form.validatorName} onChange={(e) => setForm((f) => ({ ...f, validatorName: e.target.value }))} required data-testid="validator-name" /></div>
                  <div className="space-y-1"><Label className="text-xs">Votre email *</Label><Input type="email" value={form.validatorEmail} onChange={(e) => setForm((f) => ({ ...f, validatorEmail: e.target.value }))} required data-testid="validator-email" /></div>
                  <div className="space-y-1"><Label className="text-xs">Commentaire (optionnel)</Label><Textarea rows={2} value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} /></div>
                  <div className="flex gap-2">
                    <Button type="button" variant="ghost" onClick={() => setDecision(null)}>Annuler</Button>
                    <Button type="submit" className={decision === "accept" ? "bg-emerald-600 hover:bg-emerald-700" : ""} variant={decision === "refuse" ? "destructive" : "default"} disabled={submitting} data-testid="submit-validation">
                      {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Confirmer
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </Card>
      <p className="text-center text-xs text-muted-foreground mt-4">NGL SERVICES — Menuiserie · Vitrerie · Serrurerie</p>
    </Shell>
  );
};

export default PublicQuotePage;
